import { z } from 'zod';
import { auditFieldsSchema, centsSchema, idSchema, isoDateSchema, paginated, paginationQuerySchema, positiveCentsSchema, } from './common.js';
/* ================================ Empresas ================================ */
/**
 * Empresa do grupo, para o financeiro (AMAI PARK, BELLA CENTER…). Cada
 * lançamento e cada banco são de uma; a baixa só sai de um banco da mesma
 * empresa do lançamento. É o recorte da DRE e dos relatórios.
 */
export const businessSchema = auditFieldsSchema.extend({
    id: idSchema,
    companyId: idSchema,
    name: z.string(),
    document: z.string().nullable(),
    /// A empresa em que Compras, RH e Clube lançam sozinhos. Só uma.
    isDefault: z.boolean(),
    isActive: z.boolean(),
    notes: z.string().nullable(),
});
export const businessInputSchema = z.object({
    name: z.string().trim().min(2, 'Informe o nome da empresa').max(120),
    document: z
        .string()
        .trim()
        .transform((v) => v.replace(/\D/g, ''))
        .refine((v) => v === '' || v.length === 14, 'O CNPJ tem 14 dígitos')
        .transform((v) => v || null)
        .optional()
        .nullable(),
    isDefault: z.boolean().default(false),
    isActive: z.boolean().default(true),
    notes: z.string().trim().max(500).optional().nullable(),
});
export const businessUpdateSchema = z.object({
    name: z.string().trim().min(2, 'Informe o nome da empresa').max(120).optional(),
    document: businessInputSchema.shape.document,
    isDefault: z.boolean().optional(),
    isActive: z.boolean().optional(),
    notes: z.string().trim().max(500).optional().nullable(),
});
/* ========================= Contas bancárias e caixas ====================== */
export const BANK_ACCOUNT_KINDS = ['BANCO', 'CAIXA'];
export const bankAccountKindSchema = z.enum(BANK_ACCOUNT_KINDS);
export const BANK_ACCOUNT_KIND_LABELS = {
    BANCO: 'Conta bancária',
    CAIXA: 'Caixa',
};
export const bankAccountSchema = auditFieldsSchema.extend({
    id: idSchema,
    companyId: idSchema,
    businessId: idSchema,
    businessName: z.string().nullable(),
    name: z.string(),
    kind: bankAccountKindSchema,
    bank: z.string().nullable(),
    agency: z.string().nullable(),
    accountNumber: z.string().nullable(),
    openingBalanceCents: z.number().int(),
    /// Saldo de abertura + baixas (já líquidas de estorno).
    currentBalanceCents: z.number().int(),
    isActive: z.boolean(),
});
export const bankAccountInputSchema = z.object({
    /// De qual empresa é a conta. Só baixa lançamento da mesma empresa.
    businessId: idSchema,
    name: z.string().trim().min(2, 'Informe o nome da conta').max(120),
    kind: bankAccountKindSchema.default('BANCO'),
    bank: z.string().trim().max(80).optional().nullable(),
    agency: z.string().trim().max(20).optional().nullable(),
    accountNumber: z.string().trim().max(30).optional().nullable(),
    openingBalanceCents: centsSchema.default(0),
    isActive: z.boolean().default(true),
});
/* ============================= Lançamentos =============================== */
export const FINANCIAL_ENTRY_TYPES = ['PAYABLE', 'RECEIVABLE'];
export const financialEntryTypeSchema = z.enum(FINANCIAL_ENTRY_TYPES);
export const FINANCIAL_ENTRY_TYPE_LABELS = {
    PAYABLE: 'A pagar',
    RECEIVABLE: 'A receber',
};
export const FINANCIAL_ENTRY_STATUSES = ['OPEN', 'PARTIALLY_PAID', 'PAID', 'CANCELLED'];
export const financialEntryStatusSchema = z.enum(FINANCIAL_ENTRY_STATUSES);
export const FINANCIAL_ENTRY_STATUS_LABELS = {
    OPEN: 'Em aberto',
    PARTIALLY_PAID: 'Parcialmente pago',
    PAID: 'Pago',
    CANCELLED: 'Cancelado',
};
export const FINANCIAL_ENTRY_ORIGINS = [
    'MANUAL',
    'PURCHASE_ORDER',
    'FISCAL_DOCUMENT',
    'PAYROLL',
    // Medição de empreitada: o módulo Obra saiu, mas as contas a pagar que
    // ele gerou ficam — dinheiro não se apaga. Só existe para o histórico.
    'WORK_MEASUREMENT',
    'MEMBERSHIP',
    'MEMBERSHIP_FEE',
];
export const financialEntryOriginSchema = z.enum(FINANCIAL_ENTRY_ORIGINS);
export const FINANCIAL_ENTRY_ORIGIN_LABELS = {
    MANUAL: 'Lançamento manual',
    PURCHASE_ORDER: 'Pedido de compra',
    FISCAL_DOCUMENT: 'Documento fiscal',
    PAYROLL: 'Folha de pagamento',
    WORK_MEASUREMENT: 'Medição de empreitada',
    MEMBERSHIP: 'Venda de plano do clube',
    MEMBERSHIP_FEE: 'Manutenção do clube',
};
export const INSTALLMENT_STATUSES = ['OPEN', 'PARTIALLY_PAID', 'PAID', 'CANCELLED'];
export const installmentStatusSchema = z.enum(INSTALLMENT_STATUSES);
export const PAYMENT_METHODS = [
    'PIX',
    'TED',
    'BOLETO',
    'DINHEIRO',
    'CARTAO',
    'CHEQUE',
    'OUTRO',
];
export const paymentMethodSchema = z.enum(PAYMENT_METHODS);
export const PAYMENT_METHOD_LABELS = {
    PIX: 'PIX',
    TED: 'TED / transferência',
    BOLETO: 'Boleto',
    DINHEIRO: 'Dinheiro',
    CARTAO: 'Cartão',
    CHEQUE: 'Cheque',
    OUTRO: 'Outro',
};
export const financialPaymentSchema = z.object({
    id: idSchema,
    installmentId: idSchema,
    bankAccountId: idSchema,
    bankAccountName: z.string().nullable(),
    paidAt: z.string(),
    amountCents: z.number().int(),
    method: paymentMethodSchema,
    notes: z.string().nullable(),
    isReversal: z.boolean(),
    reversesId: idSchema.nullable(),
    reason: z.string().nullable(),
    createdAt: z.string(),
    createdById: idSchema.nullable(),
    /// Quem deu a baixa — o "usuário baixa" que qualquer auditoria pergunta.
    createdByName: z.string().nullable(),
});
export const financialInstallmentSchema = z.object({
    id: idSchema,
    entryId: idSchema,
    number: z.number().int(),
    dueDate: z.string(),
    amountCents: z.number().int(),
    paidCents: z.number().int(),
    /// amountCents - paidCents. Vem calculado para a tela não repetir a conta.
    openCents: z.number().int(),
    status: installmentStatusSchema,
    /// Vencida = em aberto com vencimento no passado. Não é status persistido:
    /// é derivado da data, senão precisaria de um job só para virar o status.
    isOverdue: z.boolean(),
    paidAt: z.string().nullable(),
    payments: z.array(financialPaymentSchema),
});
export const financialEntrySchema = auditFieldsSchema.extend({
    id: idSchema,
    companyId: idSchema,
    code: z.string(),
    type: financialEntryTypeSchema,
    personId: idSchema.nullable(),
    personName: z.string().nullable(),
    description: z.string(),
    documentNumber: z.string().nullable(),
    issueDate: z.string(),
    competenceDate: z.string(),
    totalCents: z.number().int(),
    paidCents: z.number().int(),
    openCents: z.number().int(),
    businessId: idSchema,
    businessName: z.string().nullable(),
    costCenterId: idSchema.nullable(),
    costCenterName: z.string().nullable(),
    chartAccountId: idSchema,
    chartAccountName: z.string().nullable(),
    status: financialEntryStatusSchema,
    origin: financialEntryOriginSchema,
    originId: idSchema.nullable(),
    notes: z.string().nullable(),
    cancelledAt: z.string().nullable(),
    cancelReason: z.string().nullable(),
    isOverdue: z.boolean(),
    nextDueDate: z.string().nullable(),
    installments: z.array(financialInstallmentSchema),
});
const installmentInputSchema = z.object({
    dueDate: isoDateSchema,
    amountCents: positiveCentsSchema,
});
export const financialEntryInputSchema = z
    .object({
    type: financialEntryTypeSchema,
    personId: idSchema.optional().nullable(),
    description: z.string().trim().min(2, 'Descreva o lançamento').max(200),
    documentNumber: z.string().trim().max(60).optional().nullable(),
    issueDate: isoDateSchema,
    competenceDate: isoDateSchema,
    /// De qual empresa do grupo é a conta — o recorte da DRE e dos relatórios.
    businessId: idSchema,
    /// Opcional: detalha o custo DENTRO da empresa, para quem quiser.
    costCenterId: idSchema.optional().nullable(),
    chartAccountId: idSchema,
    notes: z.string().trim().max(2000).optional().nullable(),
    installments: z.array(installmentInputSchema).min(1, 'Informe ao menos uma parcela'),
})
    .refine((v) => v.installments.every((i) => i.amountCents > 0), {
    message: 'Toda parcela precisa de um valor maior que zero',
    path: ['installments'],
});
export const financialEntryUpdateSchema = z.object({
    personId: idSchema.optional().nullable(),
    description: z.string().trim().min(2).max(200).optional(),
    documentNumber: z.string().trim().max(60).optional().nullable(),
    issueDate: isoDateSchema.optional(),
    competenceDate: isoDateSchema.optional(),
    /// Trocar de empresa só enquanto não há baixa: a baixa saiu do banco dela.
    businessId: idSchema.optional(),
    costCenterId: idSchema.optional().nullable(),
    chartAccountId: idSchema.optional(),
    notes: z.string().trim().max(2000).optional().nullable(),
});
/** Baixa de parcela. Pagamento parcial é permitido — a obra paga em pedaços. */
export const payInstallmentSchema = z.object({
    bankAccountId: idSchema,
    paidAt: z
        .string()
        .refine((v) => !Number.isNaN(Date.parse(v)), { message: 'Data de pagamento inválida' })
        // Baixa pré-datada inflaria o saldo realizado de hoje; 1 dia de folga cobre
        // a diferença de fuso entre o navegador do operador e o servidor.
        .refine((v) => Date.parse(v) <= Date.now() + 24 * 60 * 60 * 1000, {
        message: 'A data do pagamento não pode estar no futuro',
    }),
    amountCents: positiveCentsSchema,
    method: paymentMethodSchema.default('PIX'),
    notes: z.string().trim().max(500).optional().nullable(),
});
/** Estorno: contra-lançamento. Exige motivo, que vai para a auditoria. */
export const reversePaymentSchema = z.object({
    reason: z.string().trim().min(5, 'Explique o motivo do estorno').max(500),
});
export const cancelEntrySchema = z.object({
    reason: z.string().trim().min(5, 'Explique o motivo do cancelamento').max(500),
});
export const financialEntryListQuerySchema = paginationQuerySchema.extend({
    type: financialEntryTypeSchema.optional(),
    status: financialEntryStatusSchema.optional(),
    personId: idSchema.optional(),
    businessId: idSchema.optional(),
    costCenterId: idSchema.optional(),
    chartAccountId: idSchema.optional(),
    /// Filtro por vencimento das parcelas.
    dueFrom: isoDateSchema.optional(),
    dueTo: isoDateSchema.optional(),
    competenceFrom: isoDateSchema.optional(),
    competenceTo: isoDateSchema.optional(),
    onlyOverdue: z
        .enum(['true', 'false'])
        .transform((v) => v === 'true')
        .optional(),
});
/**
 * A listagem financeira devolve, além da página, os TOTAIS do filtro inteiro:
 * o operador precisa saber quanto está em aberto e vencido no recorte, não só
 * nas 25 linhas visíveis.
 */
export const financialEntryListResponseSchema = paginated(financialEntrySchema).extend({
    summary: z.object({
        totalCents: z.number().int(),
        openCents: z.number().int(),
        overdueCents: z.number().int(),
    }),
});
/* ============================== Relatórios =============================== */
/** Agenda de vencimentos — a tela "o que vence esta semana / este mês". */
export const dueScheduleQuerySchema = z.object({
    from: isoDateSchema,
    to: isoDateSchema,
    type: financialEntryTypeSchema.default('PAYABLE'),
    businessId: idSchema.optional(),
});
export const dueScheduleDaySchema = z.object({
    date: z.string(),
    totalCents: z.number().int(),
    count: z.number().int(),
    overdue: z.boolean(),
    items: z.array(z.object({
        installmentId: idSchema,
        entryId: idSchema,
        code: z.string(),
        description: z.string(),
        personName: z.string().nullable(),
        businessName: z.string().nullable(),
        costCenterName: z.string().nullable(),
        number: z.number().int(),
        totalInstallments: z.number().int(),
        openCents: z.number().int(),
        status: installmentStatusSchema,
    })),
});
export const dueScheduleResponseSchema = z.object({
    days: z.array(dueScheduleDaySchema),
    totalCents: z.number().int(),
    overdueCents: z.number().int(),
});
/**
 * Fluxo de caixa: realizado (o que já foi pago/recebido) + projetado (o que
 * ainda vence) por período, filtrável por centro de custo.
 */
export const CASH_FLOW_GRANULARITIES = ['day', 'week', 'month'];
export const cashFlowGranularitySchema = z.enum(CASH_FLOW_GRANULARITIES);
export const cashFlowQuerySchema = z.object({
    from: isoDateSchema,
    to: isoDateSchema,
    granularity: cashFlowGranularitySchema.default('month'),
    businessId: idSchema.optional(),
    costCenterId: idSchema.optional(),
});
export const cashFlowBucketSchema = z.object({
    period: z.string(),
    realizedInCents: z.number().int(),
    realizedOutCents: z.number().int(),
    projectedInCents: z.number().int(),
    projectedOutCents: z.number().int(),
    netCents: z.number().int(),
    accumulatedCents: z.number().int(),
});
export const cashFlowResponseSchema = z.object({
    buckets: z.array(cashFlowBucketSchema),
    totals: z.object({
        realizedInCents: z.number().int(),
        realizedOutCents: z.number().int(),
        projectedInCents: z.number().int(),
        projectedOutCents: z.number().int(),
    }),
});
/**
 * Custo por centro de custo. É o relatório que justifica o par obrigatório
 * centro de custo + plano de contas em todo lançamento.
 */
export const COST_REPORT_GROUPINGS = ['business', 'costCenter', 'chartAccount', 'person'];
export const costReportGroupingSchema = z.enum(COST_REPORT_GROUPINGS);
export const COST_REPORT_GROUPING_LABELS = {
    business: 'Empresa',
    costCenter: 'Centro de custo',
    chartAccount: 'Conta do plano de contas',
    person: 'Fornecedor',
};
export const costReportQuerySchema = z.object({
    from: isoDateSchema,
    to: isoDateSchema,
    groupBy: costReportGroupingSchema.default('costCenter'),
    businessId: idSchema.optional(),
    /// Restringe a uma subárvore de centro de custo (inclui os filhos).
    costCenterId: idSchema.optional(),
    /// `competencia` usa a data de competência; `caixa`, a data do pagamento.
    basis: z.enum(['competencia', 'caixa']).default('competencia'),
});
export const costReportRowSchema = z.object({
    key: z.string(),
    code: z.string().nullable(),
    label: z.string(),
    entryCount: z.number().int(),
    committedCents: z.number().int(),
    paidCents: z.number().int(),
    openCents: z.number().int(),
    sharePercent: z.number(),
});
export const costReportResponseSchema = z.object({
    rows: z.array(costReportRowSchema),
    totals: z.object({
        committedCents: z.number().int(),
        paidCents: z.number().int(),
        openCents: z.number().int(),
    }),
});
/* ================================== DRE ================================== */
/**
 * Demonstração do Resultado do período.
 *
 * As linhas de resultado (lucro bruto, lucro operacional, lucro líquido) são
 * CALCULADAS aqui — não existem como conta no plano de propósito: conta de
 * resultado aceitaria lançamento e o número passaria a mentir.
 *
 * Investimento (CAPEX) fica FORA do resultado. Comprar um brinquedo não é
 * despesa do mês, é troca de caixa por patrimônio; misturar afundaria o lucro
 * de qualquer mês de obra. Aparece à parte, junto da geração de caixa.
 */
export const dreQuerySchema = z.object({
    from: isoDateSchema,
    to: isoDateSchema,
    /// `competencia` usa a data de competência; `caixa`, a data do pagamento.
    basis: z.enum(['competencia', 'caixa']).default('competencia'),
    /// A DRE de UMA empresa do grupo. Sem filtro, o consolidado.
    businessId: idSchema.optional(),
    /// Restringe a uma subárvore de centro de custo (inclui os filhos).
    costCenterId: idSchema.optional(),
});
/** Uma conta analítica dentro de um bloco da DRE. */
export const dreAccountSchema = z.object({
    chartAccountId: idSchema,
    code: z.string(),
    name: z.string(),
    amountCents: z.number().int(),
    entryCount: z.number().int(),
    /// Peso da conta dentro do bloco dela.
    sharePercent: z.number(),
});
export const DRE_SECTIONS = [
    'receitaBruta',
    'deducoes',
    'custosDiretos',
    'despesasOperacionais',
    'impostos',
    'receitasFinanceiras',
    'despesasFinanceiras',
    'investimentos',
];
export const dreSectionSchema = z.enum(DRE_SECTIONS);
export const DRE_SECTION_LABELS = {
    receitaBruta: 'Receita bruta',
    deducoes: 'Deduções de receita',
    custosDiretos: 'Custos operacionais diretos',
    despesasOperacionais: 'Despesas operacionais',
    impostos: 'Impostos e obrigações',
    receitasFinanceiras: 'Receitas financeiras e aportes',
    despesasFinanceiras: 'Despesas financeiras',
    investimentos: 'Investimentos (CAPEX)',
};
export const dreBlockSchema = z.object({
    section: dreSectionSchema,
    label: z.string(),
    totalCents: z.number().int(),
    accounts: z.array(dreAccountSchema),
});
export const dreResponseSchema = z.object({
    from: isoDateSchema,
    to: isoDateSchema,
    basis: z.enum(['competencia', 'caixa']),
    blocks: z.array(dreBlockSchema),
    /** As linhas calculadas, na ordem em que se lê a DRE. */
    result: z.object({
        receitaBrutaCents: z.number().int(),
        deducoesCents: z.number().int(),
        receitaLiquidaCents: z.number().int(),
        custosDiretosCents: z.number().int(),
        lucroBrutoCents: z.number().int(),
        despesasOperacionaisCents: z.number().int(),
        lucroOperacionalCents: z.number().int(),
        impostosCents: z.number().int(),
        resultadoFinanceiroCents: z.number().int(),
        lucroLiquidoCents: z.number().int(),
        /// Fora do resultado — entra só na geração de caixa.
        investimentosCents: z.number().int(),
        geracaoCaixaCents: z.number().int(),
    }),
    /** Margens sobre a receita líquida. Nulas quando não houve receita. */
    margins: z.object({
        brutaPercent: z.number().nullable(),
        operacionalPercent: z.number().nullable(),
        liquidaPercent: z.number().nullable(),
    }),
});
/* ===================== Fechamento de período (trava) ===================== */
/**
 * Fechar até uma data trava lançamentos, baixas e estornos com data dentro do
 * período. Reabrir (voltar a data, ou limpar) também passa por aqui — e tudo
 * fica na auditoria.
 */
export const closePeriodSchema = z.object({
    until: isoDateSchema.nullable(),
});
/* ==================== Transferência entre contas/caixas ================== */
export const bankTransferSchema = z.object({
    id: idSchema,
    companyId: idSchema,
    code: z.string(),
    fromAccountId: idSchema,
    fromAccountName: z.string().nullable(),
    toAccountId: idSchema,
    toAccountName: z.string().nullable(),
    amountCents: z.number().int(),
    transferredAt: z.string(),
    notes: z.string().nullable(),
    isReversal: z.boolean(),
    reversesId: idSchema.nullable(),
    reason: z.string().nullable(),
    createdAt: z.string(),
    createdByName: z.string().nullable(),
});
export const bankTransferInputSchema = z
    .object({
    fromAccountId: idSchema,
    toAccountId: idSchema,
    amountCents: positiveCentsSchema,
    transferredAt: isoDateSchema,
    notes: z.string().trim().max(300).optional().nullable(),
})
    .refine((v) => v.fromAccountId !== v.toAccountId, {
    message: 'A origem e o destino precisam ser contas diferentes',
    path: ['toAccountId'],
});
export const bankTransferListResponseSchema = z.object({
    data: z.array(bankTransferSchema),
});
/* ===================== Condições de pagamento (presets) ================== */
/**
 * Condições clássicas do balcão: dias corridos a partir da EMISSÃO. O valor é
 * dividido igualmente, com a sobra de centavos na primeira parcela.
 */
export const PAYMENT_TERM_PRESETS = [
    { key: 'AVISTA', label: 'À vista', days: [0] },
    { key: 'D28', label: '28 dias', days: [28] },
    { key: 'D30', label: '30 dias', days: [30] },
    { key: 'D30_60', label: '30/60', days: [30, 60] },
    { key: 'D30_60_90', label: '30/60/90', days: [30, 60, 90] },
    { key: 'D60_90_120', label: '60/90/120', days: [60, 90, 120] },
    { key: 'ENTRADA_30_60', label: 'Entrada + 30/60', days: [0, 30, 60] },
];
//# sourceMappingURL=financeiro.js.map